"use client";

import * as React from "react";
import { Bell, LogOut, User } from "lucide-react";
import { useAuth } from "@workspace/ui/hooks/useAuth";
import { UserAvatar } from "@workspace/ui/components/avatar";
import { Button } from "@workspace/ui/components/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@workspace/ui/components/dropdown-menu";
import Link from "next/link";

const authUrl = process.env.NEXT_PUBLIC_AUTH_URL || "https://auth.gaz.codes";

/**
 * The web app's origin, which hosts notification settings.
 *
 * Derived rather than given its own `NEXT_PUBLIC_*` var: web sits at the stage
 * root and auth is always one label below it (`auth.l.gaz.codes` →
 * `l.gaz.codes`), so this is right on every stage without touching every app's
 * env.
 *
 * It must be web's origin, not the current app's: a push subscription is
 * scoped to the origin that was installed, and web is the one we install. In
 * that installed app there is no address bar, so this menu is the only way to
 * reach the page that enables push.
 */
const webUrl = authUrl.replace("//auth.", "//");

export function AccountMenu() {
  const { user, authenticated, logout } = useAuth();

  // Build the login URL with a return_to pointing back at the current page, so
  // the auth app's useReturnTo() bounces the user here after sign-in instead of
  // stranding them on /profile. Computed in an effect (not at render) because
  // window.location.href isn't available during SSR — until it resolves we link
  // to bare /login, matching this component's existing post-mount swap pattern.
  const [loginHref, setLoginHref] = React.useState(`${authUrl}/login`);
  React.useEffect(() => {
    const returnTo = encodeURIComponent(window.location.href);
    setLoginHref(`${authUrl}/login?return_to=${returnTo}`);
  }, []);

  // useAuth is SSR-stable: cookieUser starts null on both SSR and first client
  // render, then hydrates in useEffect. So the very first render here is
  // always !authenticated → Sign in button. The avatar swaps in after the
  // mount effect fires. No skeleton placeholder needed — Sign-in → avatar is
  // a single, harmless swap, and there's never a hydration mismatch.
  if (!authenticated || !user) {
    return (
      <Link href={loginHref}>
        <Button size="sm" className="rounded-full">
          Sign in
        </Button>
      </Link>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="rounded-full outline-none focus-visible:ring-2 focus-visible:ring-ring">
          <UserAvatar
            email={user.email}
            username={user.initials ? undefined : user.name}
            initials={user.initials}
            avatarUrl={user.avatar}
            backgroundColor={user.avatarBgColor}
            size="sm"
          />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel>
          <div className="flex flex-col">
            <span className="text-sm font-medium">{user.name}</span>
            <span className="text-xs text-muted-foreground">{user.email}</span>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link href={`${authUrl}/profile`}>
            <User className="mr-2 h-4 w-4" />
            Profile
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <a href={`${webUrl}/settings/notifications`}>
            <Bell className="mr-2 h-4 w-4" />
            Notifications
          </a>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={logout}>
          <LogOut className="mr-2 h-4 w-4" />
          Logout
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
